import { useEffect, useState, } from "react";
import { useParams, useLocation } from "react-router-dom";
import { auth } from "../../firebase"
import { useAuth } from "../../AuthContext";
import './Player.sass'
import DropdownSelect from "components/Dropdown/DropdownSelect";
import { PlaySquare } from "lucide-react";
import useIsMobile from "hooks/isMobile";

export default function Player() {
    const { leagueId, playerId } = useParams();
    const { state } = useLocation();

    const [selectedSeason, setSelectedSeason] = useState({});
    const [selectedSeasonValue, setSelectedSeasonValue] = useState("");
    const [selectedSeasonStats, setSelectedSeasonStats] = useState({});
    const [allSeasonStats, setAllSeasonStats] = useState([]);
    const [playerRounds, setPlayerRounds] = useState([]);
    const [hasMoreRounds, setHasMoreRounds] = useState(false);
    const [nextCursor, setNextCursor] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [showBestRounds, setShowBestRounds] = useState(false);
    const [editPlayer, setEditPlayer] = useState(false);

    const [preview, setPreview] = useState(null);
    const [avatar, setAvatar] = useState(null);

    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [hcp, setHcp] = useState("");

    const { user } = useAuth();
    const isMobile = useIsMobile();

    const date = new Date(state.player.joinedAt._seconds * 1000);
    const membersYear = date.getFullYear();



    const onChangeStats = (season) => {
        const seasonValue = season.seasonId ?? season.id ?? season.value ?? "";

        setSelectedSeasonValue(seasonValue);

        if (seasonValue === "all") {
            const merged = mergeStats(allSeasonStats.filter(item => (item.value ?? item.seasonId) !== "all"));
            setSelectedSeasonStats(merged);
            return;
        }

        setSelectedSeasonStats(season);
    };



    useEffect(() => {
        fetch(`/api/league/${leagueId}/player/${playerId}/stats`)
            .then((res) => res.json())
            .then((data) => {
                console.log(data);
                const seasonStats = [
                    ...data,
                    { seasonName: "Alla", value: "all" }
                ];

                setAllSeasonStats(seasonStats);

                if (data.length > 0) {
                    setSelectedSeasonValue(data[0].seasonId);
                    setSelectedSeasonStats(data[0]);
                }
            })
            .catch((err) => {
                console.error("Failed to fetch player stats:", err);
            });
    }, [leagueId, playerId]);


    const isAllSeasons = !selectedSeasonValue || selectedSeasonValue === "all";

    useEffect(() => {
        if (isAllSeasons && showBestRounds) {
            setShowBestRounds(false);
        }
    }, [isAllSeasons]);

    useEffect(() => {
        if (showBestRounds && isAllSeasons) return;

        const pageSize = isMobile ? 10 : 20;
        const params = new URLSearchParams();

        if (showBestRounds) {
            params.set("bestRounds", "true");
            params.set("seasonId", selectedSeasonValue);
        } else {
            params.set("limit", pageSize);
        }

        fetch(`/api/league/${leagueId}/player/${playerId}/rounds?${params.toString()}`)
            .then((res) => res.json())
            .then((data) => {
                setPlayerRounds(data.rounds || []);
                setHasMoreRounds(data.hasMore || false);
                setNextCursor(data.nextCursor || null);
            })
            .catch((err) => {
                console.error("Failed to fetch player rounds:", err);
            });
    }, [leagueId, playerId, isMobile, showBestRounds, selectedSeasonValue]);

    const loadMoreRounds = () => {
        if (!nextCursor || loadingMore || showBestRounds) return;
        const pageSize = isMobile ? 10 : 20;
        setLoadingMore(true);
        fetch(`/api/league/${leagueId}/player/${playerId}/rounds?limit=${pageSize}&startAfter=${nextCursor}`)
            .then((res) => res.json())
            .then((data) => {
                setPlayerRounds(prev => [...prev, ...(data.rounds || [])]);
                setHasMoreRounds(data.hasMore || false);
                setNextCursor(data.nextCursor || null);
            })
            .catch((err) => {
                console.error("Failed to fetch more rounds:", err);
            })
            .finally(() => setLoadingMore(false));
    };

    const toggleBestRounds = () => {
        setShowBestRounds(prev => !prev);
    };

    useEffect(() => {
        if (!editPlayer && firstName !== "") return;

        fetch(`/api/league/${leagueId}/player/${playerId}`)
            .then((res) => res.json())
            .then((data) => {
                setFirstName(data.firstName || "");
                setLastName(data.lastName || "");
                setHcp(data.hcp ?? "");
                setPreview(data.avatar?.url || data.avatarUrl || null);
            })
            .catch((err) => {
                console.error("Failed to fetch player data:", err);
            });
    }, [editPlayer, leagueId, playerId]);

    
    const toggleEdit = () => {
        setEditPlayer((prev) => !prev);
    };

    const handleCancel = () => {
        setEditPlayer(false);
        setAvatar(null);
        setPreview(null);
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0] || null;
        setAvatar(selectedFile);

        if (selectedFile) {
            setPreview(URL.createObjectURL(selectedFile));
        } else {
            setPreview(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const user = auth.currentUser;
        const token = user ? await user.getIdToken() : null;

        const formData = new FormData();
        formData.append("firstName", firstName);
        formData.append("lastName", lastName);
        formData.append("hcp", hcp);

        if (avatar) {
            formData.append("avatar", avatar);
        }

        try {
            const res = await fetch(
                `/api/league/${leagueId}/player/${playerId}`,
                {
                    method: "PUT",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    body: formData
                }
            );

            const data = await res.json();

            if (!res.ok) {
                console.error("Failed to update player:", data);
                return;
            }

            setSelectedSeasonStats((prev) => ({
                ...prev,
                firstName,
                lastName,
                hcp,
                ...(data.avatar ? { avatar: data.avatar } : {}),
            }));

            setEditPlayer(false);
            setAvatar(null);

            if (data.avatar?.url) {
                setPreview(data.avatar.url);
            }

            console.log("Player updated:", data);
        } catch (err) {
            console.error("Error updating player:", err);
        }
    };



    const translations = {
        totalPrizeMoney: "Pengar inspelat",
        worstGrossScore: "Sämsta score",
        netScoreSum: "Netto totalt",
        totalPoints: "Totalt poäng",
        totalEagles: "Eagles",
        bestGrossScore: "Bästa score",
        totalExtraPoints: "Extra poäng",
        roundsPlayed: "Spelade rundor",
        totalBirdies: "Birdies",
        totalHIO: "HIO",
        grossScoreSum: "Brutto totalt",
        wins: "Vinster",
    };


    function mergeStats(arr) {
        return arr.reduce((acc, obj) => {
            Object.keys(obj).forEach(key => {
                const value = obj[key];

                if (typeof value === "number") {
                    if (key === "bestGrossScore") {
                        acc[key] = acc[key] !== undefined
                            ? Math.min(acc[key], value)
                            : value;

                    } else if (key === "worstGrossScore") {
                        acc[key] = acc[key] !== undefined
                            ? Math.max(acc[key], value)
                            : value;

                    } else {
                        acc[key] = (acc[key] || 0) + value;
                    }

                } else if (Array.isArray(value)) {
                    acc[key] = (acc[key] || []).concat(value);

                } else if (!(key in acc)) {
                    acc[key] = value;
                }
            });

            return acc;
        }, {});
    }

    return (
        <div className="player-content">
            <div className="player-card-stats">
                <img src={state.player?.avatar?.url ? state.player?.avatar?.url : "https://storage.googleapis.com/threeputt-api.firebasestorage.app/players/b80469b1-8aee-43f1-b849-15c409ba90bf/avatar-1773758210609.jpg"} alt={selectedSeasonStats.firstName} />

                <div className="player-info">
                    <h2>
                        {state.player.firstName} {state.player.lastName}
                    </h2>
                    <p>Medlem {membersYear} </p>

                </div>

                {user ?
                    <button className="button button--standard" onClick={toggleEdit}>
                        {editPlayer ? "Stäng" : "Edit"}
                    </button> :
                    ""}

            </div>

            {editPlayer && (
                <div className="edit-player-container">
                    <form name="edit-player" onSubmit={handleSubmit}>
                        <div className="form-group form-group--small">
                            <label htmlFor="firstName">Förnamn</label>
                            <input
                                id="firstName"
                                name="firstName"
                                type="text"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                            />
                        </div>

                        <div className="form-group form-group--small">
                            <label htmlFor="lastName">Efternamn</label>
                            <input
                                id="lastName"
                                name="lastName"
                                type="text"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                            />
                        </div>

                        <div className="form-group form-group--small">
                            <label htmlFor="hcp">HCP</label>
                            <input
                                id="hcp"
                                name="hcp"
                                type="number"
                                step="0.1"
                                value={hcp}
                                onChange={(e) => setHcp(e.target.value)}
                            />
                        </div>

                        <div className="form-group form-group--small">
                            <label htmlFor="avatar">Profilbild</label>
                            <input
                                id="avatar"
                                name="avatar"
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                            />
                        </div>

                        {preview && (
                            <div style={{ marginTop: 10 }}>
                                <p>Preview:</p>
                                <img
                                    src={preview}
                                    alt="Preview"
                                    style={{
                                        width: 200,
                                        height: "auto",
                                        borderRadius: 6,
                                        border: "1px solid #ccc",
                                    }}
                                />
                            </div>
                        )}

                        <button type="submit" style={{ marginTop: 15 }}>
                            Spara
                        </button>
                        <button
                            type="button"
                            style={{ marginTop: 15 }}
                            onClick={handleCancel}
                        >
                            Avbryt
                        </button>
                    </form>
                </div>
            )}



            <div className="player-stats">
                <div className="player-stats--filters">
                    <DropdownSelect
                        items={allSeasonStats}
                        value={selectedSeasonValue}
                        onChange={onChangeStats}
                        placeholder="Välj säsong"
                    />
                </div>

                <div className="stats-box-container">
                    <div className="stats-box-grid">
                        <div className="stats-box">
                            <p className="stat-name">{translations["roundsPlayed"]}</p>
                            <h4 className="stat-value">{selectedSeasonStats.roundsPlayed}</h4>
                            <p className="stat-info"></p>
                        </div>
                        <div className="stats-box">
                            <p className="stat-name">{translations["totalPrizeMoney"]}</p>
                            <h4 className="stat-value">{selectedSeasonStats.totalPrizeMoney}</h4>
                            <p className="stat-info"></p>
                        </div>

                        <div className="stats-box">
                            <p className="stat-name">{translations["wins"]}</p>
                            <h4 className="stat-value">{selectedSeasonStats.wins}</h4>
                            <p className="stat-info"></p>
                        </div>
                        <div className="stats-box">
                            <p className="stat-name">{translations["totalPoints"]}</p>
                            <h4 className="stat-value">{selectedSeasonStats.totalPoints}</h4>
                            <p className="stat-info"></p>
                        </div>

                    </div>
                    <div className="stats-panel">
                        <h4 className="panel-title">
                            Scoring
                        </h4>
                        <div>

                        </div>

                        <div className="panel-item">
                            <p className="stat-name">{translations["netScoreSum"]}</p>
                            <h4 className="stat-value">{selectedSeasonStats.netScoreSum}</h4>

                        </div>
                        <div className="panel-item">
                            <p className="stat-name">{translations["bestGrossScore"]}</p>
                            <h4 className="stat-value stat-value--green">{selectedSeasonStats.bestGrossScore}</h4>

                        </div>

                        <div className="panel-item">
                            <p className="stat-name">{translations["grossScoreSum"]}</p>
                            <h4 className="stat-value">{selectedSeasonStats.grossScoreSum}</h4>

                        </div>

                        <div className="panel-item">
                            <p className="stat-name">{translations["worstGrossScore"]}</p>
                            <h4 className="stat-value stat-value--red">{selectedSeasonStats.worstGrossScore}</h4>
                        </div>

                        <div className="panel-row">
                            <div className="">
                                <p className="stat-name">{translations["totalBirdies"]}</p>
                                <h4 className="stat-value">{selectedSeasonStats.totalBirdies}</h4>

                            </div>
                            <div className="">
                                <p className="stat-name">{translations["totalEagles"]}</p>
                                <h4 className="stat-value">{selectedSeasonStats.totalEagles}</h4>

                            </div>
                            <div className="">
                                <p className="stat-name">{translations["totalHIO"]}</p>
                                <h4 className="stat-value">{selectedSeasonStats.totalHIO}</h4>

                            </div>

                        </div>
                        <div className="panel-row">


                        </div>


                    </div>
                </div>




            </div>

            <div className="player-rounds">

                {!isAllSeasons && (
                    <div className="form-group form-group--horizontal toggle best-rounds-toggle">
                        <span>Visa 8 bästa rundorna</span>
                        <label htmlFor="showBestRounds" className="switch">
                            <input
                                type="checkbox"
                                id="showBestRounds"
                                name="showBestRounds"
                                checked={showBestRounds}
                                onChange={toggleBestRounds}
                            />
                            <span className="slider"></span>
                        </label>
                    </div>
                )}

                <table className="table playerScore-table" style={{ marginTop: '1rem' }}>
                    <thead>
                        <tr>
                            <th>DATUM</th>
                            <th>BANA</th>
                            <th>POÄNG</th>
                            <th>NETTO</th>
                            <th>BRUTTO</th>
                            {isMobile ? "" : <th>BIRDIES</th>}

                        </tr>
                    </thead>
                    <tbody>
                        {playerRounds.map((round, index) => {
                            return <tr
                                className={`round-wrapper`}
                                key={round.id}

                            >
                                <td>{round.date}</td>
                                <td>
                                    <p className="bold">{round.club}</p>
                                    <p>
                                        <span className="bold">{round.course}</span>  <span>{`${round.tee ? `- ${round.tee}` : ""}`}</span>
                                    </p>
                                </td>
                                <td>
                                    {round.score.points}
                                    {round.score.extraPoints ? (
                                        <span className="extra-points-badge"> (+{round.score.extraPoints})</span>
                                    ) : ""}
                                </td>
                                <td>{round.score.netScore}</td>
                                <td>{round.score.grossScore}</td>
                                {isMobile ? "" : <td>{round.score.birdies}</td>}

                            </tr>
                        })}
                    </tbody>
                </table>

                {hasMoreRounds && (
                    <p
                        onClick={loadMoreRounds}
                        style={{ textAlign: 'center', padding: '0.75rem', fontSize: '0.85rem', color: '#12452D', cursor: 'pointer', fontWeight: 600 }}
                    >
                        {loadingMore ? "Laddar..." : "Visa fler"}
                    </p>
                )}

            </div>
        </div>
    );
}